import { useState, useRef } from 'react';
import { Invoice, InvoiceStatus, InvoicePayMethod } from '../../../mocks/invoices';
import { agencyInfo } from '../../../mocks/dashboard';

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  Paid: 'bg-emerald-100 text-emerald-700',
  Partial: 'bg-amber-100 text-amber-700',
  Unpaid: 'bg-rose-100 text-rose-700',
  Overdue: 'bg-red-100 text-red-700',
  Draft: 'bg-gray-100 text-gray-600',
  Cancelled: 'bg-gray-100 text-gray-500',
};

interface InvoiceDetailModalProps {
  invoice: Invoice;
  onClose: () => void;
}

export default function InvoiceDetailModal({ invoice, onClose }: InvoiceDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'invoice' | 'payments' | 'pdf'>('invoice');
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<InvoicePayMethod>('Bank Transfer');
  const [payRef, setPayRef] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [paySuccess, setPaySuccess] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const brand = agencyInfo.brandColor;

  const fmtAmount = (n: number) =>
    `${invoice.currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handlePrint = () => {
    const printContents = printRef.current?.innerHTML;
    if (!printContents) return;
    const w = window.open('', '', 'width=900,height=700');
    if (!w) return;
    w.document.write(`
      <html><head><title>${invoice.number}</title>
      <style>
        body { font-family: 'Helvetica Neue', sans-serif; margin: 0; padding: 32px; color: #1a1a1a; }
        * { box-sizing: border-box; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px 12px; }
        th { background: #f8f8f8; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #888; text-align: left; }
        .text-right { text-align: right; }
        .divider { border: none; border-top: 1px solid #e5e5e5; margin: 20px 0; }
        @media print { body { padding: 0; } }
      </style>
      </head><body>${printContents}</body></html>
    `);
    w.document.close();
    w.focus();
    w.print();
    w.close();
  };

  const handleRecordPayment = (e: React.FormEvent) => {
    e.preventDefault();
    setPaySuccess(true);
    setTimeout(() => { setPaySuccess(false); setPayAmount(''); setPayRef(''); setPayNotes(''); }, 3000);
  };

  const paidPct = invoice.totalAmount > 0 ? Math.min(100, (invoice.paidAmount / invoice.totalAmount) * 100) : 0;

  const tabs = [
    { id: 'invoice', label: 'Invoice', icon: 'ri-file-text-line' },
    { id: 'payments', label: 'Payments', icon: 'ri-bank-card-line' },
    { id: 'pdf', label: 'PDF Preview', icon: 'ri-printer-line' },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-white h-full flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${brand}15` }}>
              <i className="ri-bill-line text-lg" style={{ color: brand }} />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-base">{invoice.number}</h2>
              <p className="text-xs text-gray-400">{invoice.customer} · {invoice.bookingId || 'Standalone'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[invoice.status]}`}>
              {invoice.status}
            </span>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.id ? 'border-current' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === tab.id ? { color: brand, borderColor: brand } : {}}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
          <div className="flex-1" />
          <div className="flex items-center gap-2 py-2">
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: brand }}>
              <i className="ri-printer-line text-sm" />
              Print / PDF
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'invoice' && (
            <div className="p-6 space-y-6">
              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-400 mb-1">Total Amount</p>
                  <p className="font-bold text-gray-800 text-lg">{fmtAmount(invoice.totalAmount)}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-400 mb-1">Paid</p>
                  <p className="font-bold text-emerald-700 text-lg">{fmtAmount(invoice.paidAmount)}</p>
                </div>
                <div className={`rounded-xl p-4 text-center ${invoice.balanceDue > 0 ? 'bg-rose-50' : 'bg-gray-50'}`}>
                  <p className="text-xs text-gray-400 mb-1">Balance Due</p>
                  <p className={`font-bold text-lg ${invoice.balanceDue > 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                    {invoice.balanceDue > 0 ? fmtAmount(invoice.balanceDue) : 'Settled'}
                  </p>
                </div>
              </div>

              {/* Payment progress */}
              <div>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                  <span>Payment collected</span>
                  <span className="font-semibold">{paidPct.toFixed(0)}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${paidPct}%`, backgroundColor: paidPct === 100 ? '#10B981' : brand }} />
                </div>
              </div>

              {/* Invoice details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider">Issued To</p>
                  <p className="font-semibold text-gray-800">{invoice.customer}</p>
                  <p className="text-gray-500 text-xs">{invoice.customerEmail}</p>
                  <p className="text-gray-500 text-xs">{invoice.customerPhone}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{invoice.customerAddress}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 uppercase font-medium tracking-wider">Invoice Details</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Invoice #</span>
                      <span className="font-medium text-gray-700">{invoice.number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Issued Date</span>
                      <span className="font-medium text-gray-700">{new Date(invoice.issuedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Due Date</span>
                      <span className="font-medium text-gray-700">{new Date(invoice.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    </div>
                    {invoice.bookingId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Booking Ref</span>
                        <span className="font-medium text-gray-700">{invoice.bookingId}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Agent</span>
                      <span className="font-medium text-gray-700">{invoice.agent}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Line Items</p>
                <div className="border border-gray-100 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-400">
                        <th className="text-left px-4 py-3 font-medium">Description</th>
                        <th className="text-center px-3 py-3 font-medium">Qty</th>
                        <th className="text-right px-3 py-3 font-medium">Unit Price</th>
                        <th className="text-right px-4 py-3 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {invoice.lineItems.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800 text-sm">{item.description}</p>
                            {item.details && <p className="text-xs text-gray-400 mt-0.5">{item.details}</p>}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-600">{item.qty}</td>
                          <td className="px-3 py-3 text-right text-gray-600">{fmtAmount(item.unitPrice)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${item.total < 0 ? 'text-emerald-600' : 'text-gray-800'}`}>
                            {item.total < 0 ? `- ${fmtAmount(Math.abs(item.total))}` : fmtAmount(item.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-1.5">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Subtotal</span>
                      <span>{fmtAmount(invoice.subtotal)}</span>
                    </div>
                    {invoice.discountAmt > 0 && (
                      <div className="flex justify-between text-sm text-emerald-600">
                        <span>Discount ({invoice.discountPct}%)</span>
                        <span>- {fmtAmount(invoice.discountAmt)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>VAT ({invoice.taxPct}%)</span>
                      <span>{fmtAmount(invoice.taxAmt)}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <span style={{ color: brand }}>{fmtAmount(invoice.totalAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {invoice.notes && (
                <div className="bg-amber-50 rounded-xl p-4 text-sm text-amber-800">
                  <p className="font-medium mb-1 text-xs uppercase tracking-wider text-amber-600">Notes</p>
                  <p className="leading-relaxed">{invoice.notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="p-6 space-y-6">
              {/* Progress */}
              <div className="bg-gray-50 rounded-xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Total Amount</p>
                    <p className="font-bold text-gray-800 text-xl">{fmtAmount(invoice.totalAmount)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 mb-0.5">Balance Due</p>
                    <p className={`font-bold text-xl ${invoice.balanceDue > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {invoice.balanceDue > 0 ? fmtAmount(invoice.balanceDue) : 'Settled'}
                    </p>
                  </div>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${paidPct}%`, backgroundColor: paidPct === 100 ? '#10B981' : brand }} />
                </div>
                <p className="text-xs text-gray-400 mt-2 text-right">{paidPct.toFixed(0)}% collected</p>
              </div>

              {/* Payment History */}
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-3">Payment History</p>
                {invoice.payments.length > 0 ? (
                  <div className="space-y-2">
                    {invoice.payments.map((p) => (
                      <div key={p.id} className="flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-xl">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-100">
                          <i className="ri-check-line text-emerald-600 text-base" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 text-sm">{fmtAmount(p.amount)}</p>
                          <p className="text-xs text-gray-400">{p.method} · Ref: {p.reference}</p>
                          {p.notes && <p className="text-xs text-gray-500 mt-0.5">{p.notes}</p>}
                        </div>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-300">
                    <i className="ri-bank-card-line text-2xl mb-2 block" />
                    <p className="text-sm">No payments recorded</p>
                  </div>
                )}
              </div>

              {/* Record Payment Form */}
              {invoice.balanceDue > 0 && (
                <div className="border border-gray-100 rounded-xl p-5">
                  <p className="font-semibold text-gray-800 text-sm mb-4 flex items-center gap-2">
                    <i className="ri-add-circle-line" style={{ color: brand }} />
                    Record New Payment
                  </p>
                  {paySuccess ? (
                    <div className="flex items-center gap-2 py-4 text-emerald-600 justify-center text-sm font-medium">
                      <i className="ri-checkbox-circle-line text-xl" />
                      Payment recorded successfully!
                    </div>
                  ) : (
                    <form onSubmit={handleRecordPayment} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Amount ({invoice.currency})</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder={String(invoice.balanceDue)}
                            value={payAmount}
                            onChange={(e) => setPayAmount(e.target.value)}
                            required
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
                          <select
                            value={payMethod}
                            onChange={(e) => setPayMethod(e.target.value as InvoicePayMethod)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none cursor-pointer"
                          >
                            {['Bank Transfer', 'Credit Card', 'Cash', 'Cheque'].map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Reference Number</label>
                        <input
                          type="text"
                          placeholder="e.g. TRF-001, CC-2026-001"
                          value={payRef}
                          onChange={(e) => setPayRef(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                        <input
                          type="text"
                          placeholder="e.g. Final balance payment"
                          value={payNotes}
                          onChange={(e) => setPayNotes(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none"
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2.5 rounded-lg text-white text-sm font-semibold cursor-pointer"
                        style={{ backgroundColor: brand }}
                      >
                        Record Payment
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'pdf' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-medium text-gray-600">Print-ready invoice preview</p>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium cursor-pointer"
                  style={{ backgroundColor: brand }}
                >
                  <i className="ri-printer-line" />
                  Print / Save PDF
                </button>
              </div>

              <div ref={printRef} className="bg-white border border-gray-200 rounded-xl p-8 text-sm">
                {/* PDF Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: brand }}>
                        {agencyInfo.logo}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{agencyInfo.name}</p>
                        <p className="text-xs text-gray-400">Travel Agency</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Dubai, UAE</p>
                  </div>
                  <div className="text-right">
                    <h1 className="text-2xl font-bold text-gray-900">INVOICE</h1>
                    <p className="font-semibold mt-1" style={{ color: brand }}>{invoice.number}</p>
                    <span className={`inline-block mt-2 text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLORS[invoice.status]}`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>

                <hr className="border-gray-100 mb-6" />

                {/* Bill To + Invoice Info */}
                <div className="grid grid-cols-2 gap-8 mb-6">
                  <div>
                    <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-2">Bill To</p>
                    <p className="font-bold text-gray-900">{invoice.customer}</p>
                    <p className="text-gray-500 text-xs mt-1">{invoice.customerEmail}</p>
                    <p className="text-gray-500 text-xs">{invoice.customerPhone}</p>
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed">{invoice.customerAddress}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 uppercase font-medium tracking-wider mb-2">Invoice Details</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-end gap-8">
                        <span className="text-gray-400">Invoice #</span>
                        <span className="font-semibold text-gray-700">{invoice.number}</span>
                      </div>
                      <div className="flex justify-end gap-8">
                        <span className="text-gray-400">Issued</span>
                        <span className="font-semibold text-gray-700">{new Date(invoice.issuedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                      </div>
                      <div className="flex justify-end gap-8">
                        <span className="text-gray-400">Due</span>
                        <span className="font-semibold text-gray-700">{new Date(invoice.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <table className="w-full mb-4 text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <th className="text-left px-4 py-3 rounded-l-lg">Description</th>
                      <th className="text-center px-3 py-3">Qty</th>
                      <th className="text-right px-3 py-3">Unit Price</th>
                      <th className="text-right px-4 py-3 rounded-r-lg">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lineItems.map((item, idx) => (
                      <tr key={item.id} className={idx % 2 === 0 ? '' : 'bg-gray-50/50'}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-800">{item.description}</p>
                          {item.details && <p className="text-xs text-gray-400 mt-0.5">{item.details}</p>}
                        </td>
                        <td className="px-3 py-3 text-center text-gray-600">{item.qty}</td>
                        <td className="px-3 py-3 text-right text-gray-600">{invoice.currency} {item.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className={`px-4 py-3 text-right font-medium ${item.total < 0 ? 'text-emerald-600' : 'text-gray-800'}`}>
                          {item.total < 0 ? `- ${invoice.currency} ${Math.abs(item.total).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `${invoice.currency} ${item.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-6">
                  <div className="w-72 space-y-2 text-sm">
                    <div className="flex justify-between text-gray-500">
                      <span>Subtotal</span>
                      <span>{invoice.currency} {invoice.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {invoice.discountAmt > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Discount ({invoice.discountPct}%)</span>
                        <span>- {invoice.currency} {invoice.discountAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-500">
                      <span>VAT ({invoice.taxPct}%)</span>
                      <span>{invoice.currency} {invoice.taxAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between font-bold text-base text-gray-900 pt-2 border-t border-gray-200">
                      <span>Total</span>
                      <span style={{ color: brand }}>{invoice.currency} {invoice.totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {invoice.paidAmount > 0 && (
                      <div className="flex justify-between text-emerald-600 text-sm">
                        <span>Amount Paid</span>
                        <span>- {invoice.currency} {invoice.paidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {invoice.balanceDue > 0 && (
                      <div className="flex justify-between font-bold text-base text-rose-600 pt-1 border-t border-gray-100">
                        <span>Balance Due</span>
                        <span>{invoice.currency} {invoice.balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {invoice.notes && (
                  <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-600">
                    <p className="font-medium text-gray-400 uppercase tracking-wider mb-1">Notes</p>
                    <p>{invoice.notes}</p>
                  </div>
                )}

                <hr className="border-gray-100 mt-6 mb-4" />
                <p className="text-xs text-center text-gray-400">
                  {agencyInfo.name} · Dubai, UAE · Thank you for your business!
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-gray-200 hover:bg-gray-50 text-gray-700 cursor-pointer whitespace-nowrap">
            <i className="ri-mail-send-line" />
            Send Invoice
          </button>
          <button className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium text-white cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: brand }}>
            <i className="ri-download-line" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
